import { useCallback, useEffect, useMemo, useState } from "react";

import { useHistory, useParams } from "react-router-dom";
import {
  AppModel,
  CustomerModel,
  type PerkField,
  PerkModel,
  type PerkModule,
  perkPlatformList,
  perkPlatformListShortened,
  perkSolutionTypeList,
  type PerkType,
  PerkTypes,
} from "@doitintl/cmp-models";
import { getCollection, type ModelTimestamp } from "@doitintl/models-any";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Autocomplete,
  type AutocompleteChangeReason,
  type AutocompleteValue,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type AutocompleteRenderGetTagProps } from "@mui/material/Autocomplete/Autocomplete";
import { type StorageReference } from "firebase/storage";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import values from "lodash/values";
import { string as YupString } from "yup";

import { PerkSolutionTypeText, PerkTypeText, PlatformText, SavePerkText } from "../../../assets/texts";
import DeleteDialog from "../../../Components/DeleteDialog";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useUnsavedChanges } from "../../../Context/UnsavedChangesContext";
import Editor from "../../../Support/Components/Editor/Editor";
import { useCustomerPerksBucket } from "../../../Support/Components/Editor/hooks";
import { type Customer } from "../../../types";
import { modelsTimestamp } from "../../../utils/firebase";
import { getPropsWrapper } from "../../../utils/muiUtils";
import { useSnackbar } from "../../Integrations/Slack/utils";
import ModuleList from "../ModuleList";
import { DomainInput } from "./DomainInput";
import { usePerkFormValidator } from "./usePerkFormValidator";

const styles = {
  input: {
    maxWidth: 500,
  },
  footer: {
    position: "sticky",
    bottom: 0,
    zIndex: 1,
    backgroundColor: "background.default",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    marginX: "-16px",
  },
  footerActionButtons: {
    height: "68px",
    maxWidth: 800,
    margin: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
};

const defaultSectionTitles = {
  checklist: "Pricing",
  agenda: "Support",
  modules: "Features",
};

const incorrectUrlErrorText = "Incorrect URL";

const solutionTypesMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 250,
    },
  },
};

// eslint-disable-next-line deprecation/deprecation
export const tagDefaultValues: Record<PerkTypes.Training | PerkTypes.ProServ, string[]> = {
  [PerkTypes.Training]: ["cre/training"],
  // eslint-disable-next-line deprecation/deprecation
  [PerkTypes.ProServ]: ["am/pro-serv"],
};

const buildFieldsDefaultValues = (type: PerkType, createMode: boolean, customer: Customer): PerkField => ({
  active: false,
  type,
  name: "",
  title: type === PerkTypes.IsvSolution && createMode ? customer.primaryDomain : "",
  shortDescription: "",
  description: "",
  platform: [],
  platformUrl: {},
  videoUrl: "",
  checklist: "",
  solutionType: [],
  deleted: false,
  zendeskTags: createMode && tagDefaultValues[type] ? tagDefaultValues[type] : [],
});

const buildPerkDefaultValues = (type: PerkType, createMode: boolean, customer: Customer) => ({
  agenda: "",
  modules: [{ title: "", description: "" }],
  fields: buildFieldsDefaultValues(type, createMode, customer),
});

export const PerkForm = ({
  bucketRef,
  isLoadingBucket,
}: {
  bucketRef: StorageReference | null;
  isLoadingBucket: boolean;
}) => {
  const history = useHistory();
  const { customerId, perkId } = useParams<{ customerId: string; perkId: string }>();

  const pathname = history.location.pathname?.replace(/\/+$/, "");
  const perkListPath = pathname.substring(0, (perkId ? pathname.indexOf("edit") : pathname.indexOf("create")) - 1);
  const originType = pathname.includes("isv-solutions") ? PerkTypes.IsvSolution : PerkTypes.Accelerator;
  const { customer } = useCustomerContext();
  const perkDefaultValues = useMemo(
    () => buildPerkDefaultValues(originType, !perkId, customer),
    [originType, perkId, customer]
  );
  const [agenda, setAgenda] = useState<string>(perkDefaultValues.agenda);
  const [modules, setModules] = useState<PerkModule[]>(cloneDeep(perkDefaultValues.modules));
  const [fields, setFields] = useState<PerkField>(cloneDeep({ ...perkDefaultValues.fields }));
  const [logoUrl, setLogoUrl] = useState("");
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDoitCustomer, setIsDoitCustomer] = useState(true);
  const [isvDomainCustomerId, setIsvDomainCustomerId] = useState("");
  const snackbar = useSnackbar();
  const [currentPerk, setCurrentPerk] = useState({
    agenda: perkDefaultValues.agenda,
    modules: cloneDeep(perkDefaultValues.modules),
    fields: cloneDeep({ ...perkDefaultValues.fields }),
  });
  const [firstTimePublished, setFirstTimePublished] = useState<ModelTimestamp | null>();
  const [typeChanged, setTypeChanged] = useState(false);

  const [sectionTitles, setSectionTitles] = useState<{
    checklist: string;
    agenda: string;
    modules: string;
  }>(defaultSectionTitles);
  const { activatePendingPrompt, clearPendingPrompt } = useUnsavedChanges();

  const perkTypeName = useMemo<string>(() => PerkTypeText[fields.type], [fields.type]);
  const allowTagging = useMemo(
    () => [PerkTypes.Accelerator, PerkTypes.Training, PerkTypes.Workshop].some((type) => type === fields.type),
    [fields.type]
  );

  const getPerksConfig = useCallback(async () => {
    const perksConfig = await getCollection(AppModel).doc("perks").get();
    const configs = perksConfig.data()?.perksType;
    const selectedPerkConfig = configs?.find((c) => c.id === fields.type);

    if (selectedPerkConfig) {
      Object.entries(defaultSectionTitles).forEach(([key, title]) => {
        if (!selectedPerkConfig[key]) {
          selectedPerkConfig[key] = title;
        }
      });
      setSectionTitles(selectedPerkConfig);
    }
  }, [fields.type]);

  useEffect(() => {
    if (!fields.deleted && !isEqual({ modules, agenda, fields }, currentPerk)) {
      activatePendingPrompt();
    } else {
      clearPendingPrompt();
    }
  }, [activatePendingPrompt, clearPendingPrompt, agenda, fields, modules, currentPerk]);

  const [isFormValid] = usePerkFormValidator({ agenda, fields, isDoitCustomer, logoUrl, modules });

  useEffect(() => {
    let isMounted = true;
    const fetchPerk = async (type?: PerkType) => {
      const perk = (await getCollection(PerkModel).doc(perkId).get()).asModelData();

      if (!perk) {
        history.push(perkListPath);
      }

      if (!isMounted) {
        // preventing updating state on unmounted component
        return;
      }

      const { modules, agenda, fields, timePublished } = perk ?? {};
      setFirstTimePublished(timePublished);

      const existentPerk = {
        modules: [] as PerkModule[],
        agenda: agenda ?? perkDefaultValues.agenda,
        fields: perkDefaultValues.fields,
      };

      if (modules?.length) {
        setModules(modules);
        modules.forEach((module) => existentPerk.modules?.push(module));
      } else {
        existentPerk.modules.push(...perkDefaultValues.modules);
      }

      if (agenda) {
        setAgenda(agenda);
      }

      const formattedFields = Object.entries(perkDefaultValues.fields).reduce(
        (next, [key, value]) => ({ ...next, [key]: fields?.[key] ?? value }),
        {}
      ) as PerkField;

      existentPerk.fields = formattedFields;
      if (typeChanged && type) {
        formattedFields.type = type;
      }
      setFields(cloneDeep(formattedFields));

      setCurrentPerk(existentPerk);

      if (perk?.fields.title) {
        const querySnapshot = await getCollection(CustomerModel).where("primaryDomain", "==", perk?.fields.title).get();
        setIsvDomainCustomerId(querySnapshot.docs[0]?.id ?? "");
      }

      setLogoUrl(perk?.enrichment?.logoUrl ?? "");
      setIsDoitCustomer(!!perk?.enrichment?.providerIsDoitCustomer);
    };

    if (perkId) {
      fetchPerk(fields?.type);
    } else {
      setLogoUrl(customer.enrichment.logo ?? "");
      if (fields.type === PerkTypes.IsvSolution) {
        setIsvDomainCustomerId(customer.id);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [
    customerId,
    history,
    perkId,
    customer,
    perkListPath,
    perkDefaultValues.agenda,
    perkDefaultValues.fields,
    perkDefaultValues.modules,
    fields.type,
    typeChanged,
  ]);

  useEffect(() => {
    getPerksConfig();
  }, [getPerksConfig]);

  const handleChange = useCallback((event) => {
    const name = event.target?.name;
    const inputValue = event.target?.value ?? event;

    setFields((f) => ({
      ...f,
      [name]: inputValue,
    }));
  }, []);

  const handleMarketplaceSelection = useCallback(
    (event, platform) => {
      const checked = event.target.checked;
      const newFields = { ...fields };

      if (checked) {
        newFields.platform.push(platform);
        allowTagging && newFields.zendeskTags.push(perkPlatformListShortened[platform]);
      } else {
        newFields.platform = newFields.platform.filter((x) => x !== platform);
        allowTagging &&
          (newFields.zendeskTags = newFields.zendeskTags.filter((x) => x !== perkPlatformListShortened[platform]));
        delete newFields.platformUrl?.[platform];
      }

      setFields(newFields);
    },
    [allowTagging, fields]
  );

  const handleMarketplaceUrlChange = useCallback((event, platform) => {
    const inputValue = event.target?.value;

    setFields((f) => {
      const tmp = { ...f };
      if (tmp.platformUrl) {
        if (inputValue) {
          tmp.platformUrl[platform] = inputValue;
        } else {
          delete tmp.platformUrl[platform];
        }
      } else {
        tmp.platformUrl = { [platform]: inputValue };
      }
      return tmp;
    });
  }, []);

  const removeEmptyModules = useCallback(
    (): PerkModule[] => modules.filter((m) => m.title || m.description),
    [modules]
  );

  const createPerk = useCallback(
    async (active) => {
      const newModules = removeEmptyModules();
      const timeNow = modelsTimestamp();
      const perk = {
        agenda: agenda ?? "",
        enrichment: { logoUrl: logoUrl ?? "", providerIsDoitCustomer: isDoitCustomer },
        fields: { ...fields, active },
        modules: newModules,
        timeModified: timeNow,
        timePublished: (firstTimePublished ?? active) ? timeNow : null,
      } as const;

      if (!perkId) {
        await getCollection(PerkModel).add({ ...perk, timeCreated: timeNow });
        if (active) {
          snackbar.onOpen({
            message: SavePerkText.PUBLISH_SUCCESS(perkTypeName),
            variant: "success",
          });
        } else {
          snackbar.onOpen({
            message: SavePerkText.SAVE_DRAFT_SUCCESS(perkTypeName),
            variant: "success",
          });
        }
      } else {
        await getCollection(PerkModel).doc(perkId).update(perk);
        snackbar.onOpen({
          message: SavePerkText.UPDATE_SUCCESS(perkTypeName),
          variant: "success",
        });
      }
      clearPendingPrompt();
      history.push(perkListPath);
    },
    [
      removeEmptyModules,
      fields,
      agenda,
      logoUrl,
      isDoitCustomer,
      firstTimePublished,
      perkId,
      clearPendingPrompt,
      history,
      perkListPath,
      snackbar,
      perkTypeName,
    ]
  );

  const handleDeletePerk = useCallback(async () => {
    await getCollection(PerkModel).doc(perkId).update("fields.deleted", true);
    setDeleteDialogOpen(false);
    setFields({ ...fields, deleted: true });
    history.push(perkListPath);
  }, [fields, history, perkId, perkListPath]);

  const handleEditorChange = (html, delta, source, editor, setter: () => void) => {
    // Blocking first api change
    if (source === "api") {
      return;
    }
    setter();
  };

  const handleIsDoitCustomerChange = useCallback(
    (value) => {
      setIsDoitCustomer(value);
      if (value) {
        setIsvDomainCustomerId(customer.id);
        setFields({ ...fields, title: customer.primaryDomain });
        setLogoUrl(customer.enrichment.logo ?? "");
      }
    },
    [customer.enrichment.logo, customer.id, customer.primaryDomain, fields]
  );

  const handleTagsChange = useCallback<
    (
      event: React.SyntheticEvent,
      value: AutocompleteValue<string, true, false, true>,
      reason: AutocompleteChangeReason
    ) => void
  >(
    (_event, newTags) => {
      const zendeskTags = newTags.map((tag) => tag.trim().toLowerCase());
      setFields({ ...fields, zendeskTags });
    },
    [fields]
  );

  const wrapGetProps: (
    func: AutocompleteRenderGetTagProps,
    index: number
  ) => ReturnType<AutocompleteRenderGetTagProps> = useCallback(getPropsWrapper, []);

  return (
    <>
      <Stack sx={{ maxWidth: 800, marginX: "auto" }} data-cy="create-update-perk">
        {isDeleteDialogOpen && (
          <DeleteDialog
            open={true}
            title={`Delete ${perkTypeName}?`}
            message={`This ${perkTypeName} will be permanently deleted and won't be able to be recovered.`}
            onDelete={handleDeletePerk}
            onClose={() => {
              setDeleteDialogOpen(false);
            }}
          />
        )}
        <Stack
          direction="row"
          sx={{
            mt: 6,
            mb: 4,
            alignItems: "center",
            gap: 3,
          }}
        >
          <Typography variant="h1" data-cy="perk-form-title">
            {perkId ? "Edit" : "Create new"} {perkTypeName}
          </Typography>
          {!!perkId && (
            <>
              {!fields.active && (
                <Chip label="Draft" data-cy="draft-chip" sx={{ background: "#D51A4E", color: "white" }} />
              )}
              {fields.active && (
                <Chip label="Published" data-cy="published-chip" sx={{ background: "#4CAF50", color: "white" }} />
              )}

              <Button
                variant="text"
                data-cy="delete-perk-button"
                color="error"
                onClick={() => {
                  setDeleteDialogOpen(true);
                }}
                size="small"
              >
                <DeleteIcon fontSize="small" />
                <Typography>Delete {perkTypeName}</Typography>
              </Button>
            </>
          )}
        </Stack>

        <Typography
          variant="h4"
          sx={{
            mb: 3,
          }}
        >
          General
        </Typography>

        <TextField
          label="Title"
          sx={styles.input}
          name="name"
          value={fields.name}
          onChange={handleChange}
          helperText={`${fields.name?.length ?? 0}/60`}
          required
          slotProps={{
            htmlInput: { maxLength: 60, "data-cy": "name-textfield" },
            formHelperText: { sx: { textAlign: "right", mb: "1rem" } },
          }}
        />
        <TextField
          label="Short description"
          sx={styles.input}
          name="shortDescription"
          value={fields.shortDescription}
          onChange={handleChange}
          helperText={`${fields.shortDescription?.length ?? 0}/60`}
          required
          slotProps={{
            htmlInput: { maxLength: 60, "data-cy": "short-description-textfield" },
            formHelperText: { style: { textAlign: "right" } },
          }}
        />
        <TextField
          label="Type"
          sx={{ ...styles.input, my: 2 }}
          name="type"
          value={fields.type}
          onChange={(event) => {
            setFields((prevFields) => {
              const newType = event.target.value as PerkType;
              const newZendeskTags = prevFields.zendeskTags.filter((tag) =>
                values(tagDefaultValues).flat().includes(tag)
              ); // strip off all default tags
              newZendeskTags.push(...(tagDefaultValues?.[newType] || [])); // add new default tags
              setTypeChanged(true);
              return {
                ...prevFields,
                type: newType,
                zendeskTags: newZendeskTags,
              };
            });
          }}
          select
          required
          data-cy="type-select"
        >
          {[PerkTypes.Accelerator, PerkTypes.IsvSolution, PerkTypes.Training, PerkTypes.Workshop].map((perkType) => (
            <MenuItem key={perkType} value={perkType} data-cy={`menu-item-${perkType}`}>
              {PerkTypeText[perkType]}
            </MenuItem>
          ))}
        </TextField>

        {fields.type === "isv-solution" && (
          <Stack
            sx={{
              mt: 3,
              mb: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
              }}
            >
              Is the provider a DoiT customer?
            </Typography>
            <RadioGroup
              sx={{ mb: 3 }}
              value={isDoitCustomer}
              onChange={(_, value) => {
                handleIsDoitCustomerChange(value === "true");
              }}
              name="is-doit-customer-radio-group"
            >
              <FormControlLabel value={true} control={<Radio data-cy="is-doit-customer-radio" />} label="Yes" />
              <FormControlLabel value={false} control={<Radio data-cy="is-not-doit-customer-radio" />} label="No" />
            </RadioGroup>

            <DomainInput
              isDoitCustomer={isDoitCustomer}
              isvDomainCustomerId={isvDomainCustomerId}
              title={fields.title}
              handleDomainChange={(value) => {
                setFields((f) => ({
                  ...f,
                  title: value,
                }));
              }}
              logoURL={logoUrl}
              handleLogoURLChange={(value) => {
                setLogoUrl(value);
              }}
            />
          </Stack>
        )}

        {!isDoitCustomer && (
          <TextField
            label="Customer logo URL"
            sx={{ ...styles.input, mt: 2, mb: 2 }}
            value={logoUrl}
            onChange={(e) => {
              setLogoUrl(e.target.value);
            }}
            data-cy="isv-non-customer-logo-textfield"
            required
            error={!YupString().url().isValidSync(logoUrl)}
            helperText={!YupString().url().isValidSync(logoUrl) && "Incorrect URL"}
          />
        )}

        {allowTagging && (
          <Autocomplete
            multiple
            freeSolo
            filterSelectedOptions
            autoSelect
            options={tagDefaultValues[fields.type] ?? []} // populate list with default tags so that people can put them back
            value={fields.zendeskTags}
            onChange={handleTagsChange}
            renderTags={(value, getTagProps) =>
              value.map((tag, index) => (
                <Box key={tag}>
                  <Chip
                    variant="outlined"
                    size="small"
                    color="primary"
                    label={tag}
                    data-cy={`zendesk-tag-${tag}`}
                    {...wrapGetProps(getTagProps, index)}
                  />
                </Box>
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ ...styles.input, minHeight: "1.5em", mt: 1 }}
                margin="dense"
                label="Zendesk tags"
                multiline
                variant="outlined"
                data-cy="zendesk-tags-textfield"
                slotProps={{
                  inputLabel: { shrink: true },
                }}
              />
            )}
          />
        )}

        <FormControl error={false} sx={{ mt: 3, mb: 4 }}>
          <Typography
            sx={{
              color: "text.secondary",
            }}
          >
            Cloud providers
          </Typography>
          <FormGroup>
            {perkPlatformList.map((p) => (
              <FormControlLabel
                key={p}
                control={
                  <Checkbox
                    onChange={(e) => {
                      handleMarketplaceSelection(e, p);
                    }}
                    checked={fields.platform?.includes(p)}
                    data-cy={`${p}-checkbox`}
                  />
                }
                label={PlatformText[p]}
              />
            ))}
          </FormGroup>
        </FormControl>

        {fields.type === "isv-solution" &&
          fields.platform?.map(
            (p) =>
              (p === "amazon-web-services" || p === "google-cloud") && (
                <TextField
                  key={p}
                  label={`${PlatformText[p]} marketplace URL`}
                  sx={{ ...styles.input, mb: 3 }}
                  value={fields.platformUrl?.[p] ?? ""}
                  onChange={(e) => {
                    handleMarketplaceUrlChange(e, p);
                  }}
                  error={!YupString().url().isValidSync(fields.platformUrl?.[p])}
                  helperText={!YupString().url().isValidSync(fields.platformUrl?.[p]) && incorrectUrlErrorText}
                  slotProps={{
                    htmlInput: { "data-cy": `${p}-url-textfield` },
                  }}
                />
              )
          )}

        {fields.type === "isv-solution" && (
          <FormControl fullWidth sx={{ ...styles.input, mb: "20px" }} size="small" variant="outlined" required>
            <InputLabel id="type_id" shrink>
              Solution type(s)
            </InputLabel>
            <Select
              label="Solution type(s)"
              labelId="solution_type_id"
              id="solution-type-select"
              name="solutionType"
              data-cy="solution-type-select"
              value={fields.solutionType}
              onChange={handleChange}
              renderValue={(selected) => selected.map((x) => PerkSolutionTypeText[x]).join(", ")}
              MenuProps={solutionTypesMenuProps}
              notched
              multiple
            >
              {perkSolutionTypeList.map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox checked={fields.solutionType && fields.solutionType.indexOf(type) > -1} />
                  <ListItemText primary={PerkSolutionTypeText[type]} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          label="YouTube video URL"
          sx={styles.input}
          name="videoUrl"
          value={fields.videoUrl}
          onChange={handleChange}
          error={!YupString().url().isValidSync(fields.videoUrl)}
          helperText={!YupString().url().isValidSync(fields.videoUrl) && incorrectUrlErrorText}
          slotProps={{
            htmlInput: { "data-cy": "videourl-textfield" },
          }}
        />

        <Typography
          variant="h4"
          data-cy="description-title"
          sx={{
            mt: 4,
            mb: 2,
          }}
        >
          Overview
        </Typography>
        <Editor
          bucketRef={bucketRef}
          isLoadingBucket={isLoadingBucket}
          resourceName={`perks/${perkId || "new "}/description`}
          value={fields.description}
          onChange={(html, delta, source, editor) => {
            handleEditorChange(html, delta, source, editor, () => {
              setFields({
                ...fields,
                description: html,
              });
            });
          }}
        />

        <Typography
          variant="h4"
          data-cy="checklist-title"
          sx={{
            mt: 4,
            mb: 2,
          }}
        >
          {sectionTitles.checklist}
        </Typography>
        <Editor
          bucketRef={bucketRef}
          isLoadingBucket={isLoadingBucket}
          resourceName={`perks/${perkId || "new "}/checklist`}
          value={fields.checklist}
          onChange={(html, delta, source, editor) => {
            handleEditorChange(html, delta, source, editor, () => {
              setFields({
                ...fields,
                checklist: html,
              });
            });
          }}
        />

        <Typography
          variant="h4"
          data-cy="agenda-title"
          sx={{
            mt: 4,
            mb: 2,
          }}
        >
          {sectionTitles.agenda}
        </Typography>
        <Editor
          bucketRef={bucketRef}
          isLoadingBucket={isLoadingBucket}
          resourceName={`perks/${perkId || "new "}/agenda`}
          value={agenda}
          onChange={(html, delta, source, editor) => {
            handleEditorChange(html, delta, source, editor, () => {
              setAgenda(html);
            });
          }}
        />

        <Typography
          variant="h4"
          data-cy="modules-title"
          sx={{
            mt: 4,
            mb: 3,
          }}
        >
          {sectionTitles.modules}
        </Typography>
        <ModuleList
          modulesData={modules}
          onUpdate={setModules}
          sectionTitle={sectionTitles.modules}
          perkId={perkId}
          bucketRef={bucketRef}
          isLoadingBucket={isLoadingBucket}
        />
      </Stack>
      <Box sx={styles.footer}>
        <Box sx={styles.footerActionButtons}>
          <Button
            data-cy="exit-page-button"
            onClick={() => {
              history.push(perkListPath);
            }}
          >
            Cancel
          </Button>
          <Stack
            direction="row"
            sx={{
              gap: 2,
            }}
          >
            <Button variant="outlined" onClick={() => createPerk(false)}>
              Save as draft
            </Button>
            <Button
              variant="contained"
              onClick={() => createPerk(true)}
              data-cy="publish-button"
              disabled={!isFormValid}
            >
              Publish
            </Button>
          </Stack>
        </Box>
      </Box>
    </>
  );
};

export default function PerkFormComponent() {
  const { bucketRef, isLoadingBucket } = useCustomerPerksBucket();
  return <PerkForm bucketRef={bucketRef} isLoadingBucket={isLoadingBucket} />;
}
